<template>
  <div class="contacts">

    <div class="page__header">
      <div class="page__title">{{ $t(`pageTitles.${"contact"}`) }}</div>

      <div class="contacts__buttons">
        <button v-if="canCreateCompany" type="button" class="button button--default button--shadow"
          @click="$store.commit('setShowAddCompanyContactModal', true)">
          {{ $t(`contact.${"addNewContact"}`) }}
        </button>
      </div>
    </div>

    <div class="contacts__content">
      <div class="contacts__list box box--default" :class="{ 'contacts__list--hide': showModels }">
        <div class="label label--gradient">{{ $t(`contact.${"companies"}`) }}</div>

        <Loading v-if="companiesLoading" />
        <template v-else>
          <div class="companies">

            <VSelect :options="authorizedCompanyTeams" :reduce="(team) => team.id" placeholder="Team" label="name"
              v-model="userTeam" class="select select--default select-column" style="margin-bottom: 15px;"
              @update:modelValue="initializeTeamData($event)" />

            <div class="companies__search" v-if="userTeam !== null">
              <form class="search" @submit.prevent>
                <input type="text" class="search__search" :class="{ 'search--active': companySearchKeywords }"
                  v-model="companySearchKeywords" />
              </form>
            </div>

            <div class="companies__list" v-if="userTeam !== null">
              <Contact v-for="company in filteredCompanies" :key="company.id" :company="company"
                :selectedCompany="selectedCompany" :selectCompany="selectCompany" />
            </div>
          </div>
        </template>
      </div>

      <div class="contacts__overview box box--default" :class="{ 'contacts__overview--toggled': showModels }">
        <div class="label label--gradient">{{ $t(`contact.${"contact"}`) }}</div>
        <Loading v-if="companiesLoading || selectingCompany || applyingServices || isUpdatingCompany" />
        <template v-else>
          <div class="contact" v-if="userTeam !== null">
            <div class="company">
              <div class="company__header">
                <div class="company__logo" v-if="userTeam != null">
                  <img v-if="selectedCompany.logo" :src="selectedCompany.logo.url" alt="" />
                </div>
                <span v-if="userTeam != null">{{ selectedCompany.name }}</span>
                <div class="company__actions">
                  <div v-if="userTeam != null && canEditCompany" class="company__edit icon icon__edit" @click="editContact()"></div>
                  <div v-if="userTeam != null && !canEditCompany" style="cursor:no-drop;" class="company__edit icon icon__edit"></div>
                  <div v-if="userTeam != null && canDeleteCompany"
                    class="company__delete icon icon__delete" @click="deleteCompanyModal(selectedCompany.id)"></div>
                  <div v-if="userTeam != null && !canDeleteCompany" style="cursor:no-drop;"
                    class="company__delete icon icon__delete"></div>
                </div>
              </div>

              <div class="company__contacts" v-if="selectedCompany && userTeam != null">
                <div class="company__contact"
                  v-if="selectedCompany.address || selectedCompany.city || selectedCompany.zip">
                  <div class="company__icon company__icon--location"></div>
                  <div class="company__detail" v-if="userTeam != null">
                    {{ selectedCompany.address }}
                  </div>
                  <div class="company__detail" v-if="selectedCompany.city">{{ selectedCompany.city }}</div>
                  <div class="company__detail" v-if="selectedCompany.zip">{{ selectedCompany.zip }}</div>
                </div>

                <div class="company__contact" v-if="selectedCompany.phone">
                  <div class="company__icon company__icon--mobile"></div>
                  <div class="company__detail">{{ selectedCompany.phone }}</div>
                </div>

                <div class="company__contact" v-if="selectedCompany.email || selectedCompany.mandate_email">
                  <div class="company__icon company__icon--mail"></div>
                  <div class="company__detail" v-if="selectedCompany.email">{{ selectedCompany.email }}</div>
                  <div class="company__detail" v-if="selectedCompany.mandate_email">{{ selectedCompany.mandate_email }}
                  </div>
                </div>

                <div class="company__contact" v-if="selectedCompany.web">
                  <div class="company__icon company__icon--web"></div>
                  <div class="company__detail">{{ selectedCompany.web }}</div>
                </div>

                <div class="company__contact">
                  <div v-if="selectedCompany.mandate_distribution_list" class="company__icon icon icon__active"></div>
                  <div v-else class="company__icon icon icon__inactive"></div>
                  <div class="company__detail">{{ $t(`addCompanyContactModal.${"mandateList"}`) }}</div>
                </div>
              </div>
            </div>

            <div class="company__contact comment" v-if="selectedCompany.comment">
              <div class="company__detail">{{ selectedCompany.comment }}</div>
            </div>

            <div class="contact__map-wrapper">
              <div class="contact__services" :class="{
                'contact__services--center': !selectedCompany.services.length,
              }">
                <div class="services">
                  <div class="service" v-for="service in selectedCompany.services" :key="service.id"
                    @click="selectService(service)">
                    {{ service.name }}
                  </div>
                  <DropdownServices class="service--add" :dropdownServices="branchCategoryServices"
                    :services="selectedCompany.services" :applyServices="applyServices"
                    :addOrRemoveServices="addOrRemoveServices" :closeServicesDropdown="closeServicesDropdown" />
                </div>
              </div>
              <div class="contact__map">
                <Map :lan="selectedCompany.lan" :lat="selectedCompany.lat" :address="companyAddress" />
              </div>
            </div>
          </div>
        </template>
      </div>

      <transition name="fade">
        <div class="contacts__models box box--default" :class="{ 'contacts__models--show': showModels }"
          v-if="showModels">
          <div class="label label--gradient">
            <span>
              {{ selectedService.name }}
              <div class="label__close" @click="showModels = false"></div>
            </span>
          </div>

          <Loading v-if="modelsLoading" />
          <template v-else>
            <div class="models">
              <NoData v-if="!models.length" :type="'models'" />
              <div class="models__list">
                <template v-if="models.length">
                  <Model v-for="model in models" :key="model.id" :model="model" :getModels="getModels" />
                </template>
              </div>
              <button class="button button--default button--shadow" v-if="models.length && canEditCompany"
                @click="$store.commit('setShowAddModelModal', true)">
                {{ $t(`contact.${"addNewModelButton"}`) }}
              </button>
            </div>
          </template>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import Contact from "@/components/Contacts/Contact";
import Loading from "@/partials/Loading";
import Map from "@/components/Contacts/Map";
import Model from "@/components/Contacts/Model";
import NoData from "@/partials/NoData";
import DropdownServices from "@/partials/DropdownServices";

const store = useStore();
const router = useRouter();

// Data
const companySearchKeywords = ref("");
const showModels = ref(false);
const selectedService = ref({
  name: "",
  id: "",
});
const modelsLoading = ref(false);
const preServices = ref([]);
const userTeam = ref(null);

const selectingCompany = ref(false);
const applyingServices = ref(false);

// Computed
const companies = computed(() => store.getters.companies);
const models = computed(() => store.getters.models);
const branchCategoryServices = computed(() => store.getters.branchCategoryServices);
const selectedCompany = computed(() => store.getters.selectedCompany);
 
const companiesLoading = computed(() => store.getters.companiesLoading);
const isUpdatingCompany = computed(() => store.getters.isUpdatingCompany);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const authorizedCompanyTeams = computed(() => {
  const authorizedIds = store.getters.authorizedTeamIds("company_access");
  if (authorizedIds === null && isSuperAdmin.value) return store.getters.teams || [];
  return authorizedIds.map((teamId) => store.getters.membershipFor(teamId)?.team).filter(Boolean);
});
const can = (ability) => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission(ability, userTeam.value);
};
const canCreateCompany = computed(() => can("company_create"));
const canEditCompany = computed(() => can("company_edit"));
const canDeleteCompany = computed(() => can("company_delete"));

const filteredCompanies = computed(() => {
  if (!companies.value || !Array.isArray(companies.value)) return [];

  const search = companySearchKeywords.value?.toLowerCase().trim() || "";

  return companies.value.filter((company) => {
    if (!company || !company.name) return false;

    return company.name
      .toLowerCase()
      .trim()
      .includes(search);
  });
});

const companyAddress = computed(() => {
  if (!selectedCompany.value) return "";

  const city = selectedCompany.value.city || "";
  const zip = selectedCompany.value.zip || "";
  const address = selectedCompany.value.address || "";

  return `${city} ${zip} ${address}`.trim();
});

// Watch
watch(
  () => selectedCompany.value?.id,
  async (companyId) => {
    if (!companyId || !selectedService.value?.id) return;

    modelsLoading.value = true;
    await getModels(selectedService.value.id, companyId);
    modelsLoading.value = false;
  }
);

// Methods
const initializeTeamData = async (teamId) => {
  try {
    store.commit("setCompaniesLoading", true);
    await store.dispatch("getBranchCategoryServices");
    await store.dispatch("getCompanies", teamId);
    const [firstCompany] = companies.value;
    if (!firstCompany) {
      console.warn("No companies found for team:", teamId);
      store.commit("setCompaniesLoading", false);
      return;
    }
    store.commit("setSelectedCompany", firstCompany);
    const [firstService] = firstCompany.services || [];
    if (firstService) {
      selectedService.value.id = firstService.id;
      selectedService.value.name = firstService[`name`];
      store.commit("setSelectedService", firstService);
      await getModels(firstService.id, firstCompany.id);
    }
    preServices.value = [...selectedCompany.value.services];
  } catch (error) {
    // Error already handled by store action
  } finally {
    store.commit("setCompaniesLoading", false);
    userTeam.value = teamId;
  }
};

// const selectCompany = (company) => {
//   store.commit("setSelectedCompany", company);
// };

const selectCompany = async (company) => {
  try {
    selectingCompany.value = true;
    const freshCompanyData = await store.dispatch("getCompany", company.id);
    store.commit("setSelectedCompany", freshCompanyData);
    preServices.value = [...freshCompanyData.services];
  } catch (err) {
    // Error already handled by store action
  } finally {
    selectingCompany.value = false;
  }
};

const selectService = (service) => {
  selectedService.value.id = service.id;
  selectedService.value.name = service[`name`];
  store.commit("setSelectedService", service);
  showModels.value = true;
};

const addOrRemoveServices = (event, service) => {
  const { checked } = event.target;
  const existingService = preServices.value.findIndex(
    (s) => s.id === service.id
  );
  if (checked) {
    if (existingService === -1) {
      preServices.value = [...preServices.value, service];
    }
  } else {
    preServices.value.splice(existingService, 1);
  }
};

const applyServices = async () => {
  const services = preServices.value.map((service) => service.id);
  const previousServices = [...selectedCompany.value.services];
  try {
    applyingServices.value = true;
    await store.dispatch("updateCompany", {
      companyId: selectedCompany.value.id,
      services,
    });
    const company = await store.dispatch(
      "getCompany",
      selectedCompany.value.id
    );

    const indexOfCompanyToReplace = companies.value.findIndex(
      (c) => c.id === company.id
    );
    const tempArray = [...companies.value];
    tempArray[indexOfCompanyToReplace] = company;
    store.commit("setSelectedCompany", company);
    store.commit("setCompanies", tempArray);
  } catch (err) {
    // Error already handled by store action
    preServices.value = [...previousServices];
  } finally {
    applyingServices.value = false;
  }
};

const closeServicesDropdown = () => {
  preServices.value = [...selectedCompany.value.services];
};

const getModels = async (
  serviceId = selectedService.value.id,
  companyId = selectedCompany.value.id
) => {
  try {
    const models = await store.dispatch(
      "getModelsByCompanyAndServices",
      {
        companyId,
        serviceId,
      }
    );
    store.commit("setModels", models);
  } catch (err) {
    // Error already handled by store action
  }
};

const editContact = () => {
  if (!canEditCompany.value) return;
  store.commit("setContactToEdit", selectedCompany.value);
  store.commit("setContactToEditTeam", userTeam.value);
  store.commit("setShowAddCompanyContactModal", true);
};

const deleteCompanyModal = (id) => {
  if (!canDeleteCompany.value) return;
  store.commit("setDeleteModal", {
    display: true,
    message: '',
    action: () => deleteSelectedCompany(id, userTeam.value),
    id: id,
  });
};

const deleteSelectedCompany = async (companyId, teamId) => {
  await store.dispatch('deleteCompany', companyId);
  await initializeTeamData(teamId);
};

// Created/Mounted logic
(async () => {
  if (store.getters.isSuperAdmin && !store.getters.teams.length) {
    await store.dispatch("getTeams");
  }

  if (!authorizedCompanyTeams.value.length && store.getters.authorizedTeamIds("company_access") !== null) {
    router.push('/dashboard');
  } else {
    if (authorizedCompanyTeams.value.length === 1) {
      store.commit("setCompaniesLoading", true);

      await store.dispatch("getBranchCategoryServices");
      await store.dispatch("getCompanies", authorizedCompanyTeams.value[0].id);
      const [firstCompany] = companies.value;
      store.commit("setSelectedCompany", firstCompany);
      const [firstService] = firstCompany.services;
      selectedService.value.id = firstService.id;
      selectedService.value.name = firstService[`name`];
      store.commit("setSelectedService", firstService);
      preServices.value = [...selectedCompany.value.services];
      await getModels(firstService.id, firstCompany.id);

      userTeam.value = authorizedCompanyTeams.value[0].id;

      store.commit("setCompaniesLoading", false);
    } else {
      store.commit("setCompaniesLoading", false);
    }
  }
})();
</script>

<style scoped lang="scss">
.contacts {
  height: 100%;

  &__content {
    display: flex;
    padding-top: 30px;
    height: calc(100% - 48px - 20px - 10px);
    gap: 32px;
    position: relative;
    overflow-x: hidden;
    align-items: flex-start;
  }

  &__buttons {
    display: flex;

    .button {
      width: 190px;

      &:first-of-type {
        margin-right: 50px;
      }
    }
  }

  &__list,
  &__overview,
  &__models {
    background: var(--bg-1);
    position: relative;
    height: 100%;
    min-width: 0;
    transition: flex-basis 0.3s ease, opacity 0.3s ease, padding-top 0.3s ease;
  }

  &__list {
    flex: 0 0 38%;
    min-width: 320px;
    max-height: calc(100vh - 220px);
  }

  &__overview {
    flex: 1 1 auto;
    min-width: 360px;
  }

  &__models {
    flex: 0 0 0;
    opacity: 0;
    padding-top: 0;
    margin-left: 20px;

    &--show {
      flex-basis: 38%;
      opacity: 1;
      padding-top: 20px;
      min-width: 320px;
    }

    span {
      position: relative;

      .label__close {
        position: absolute;
        top: -25px;
        right: -35px;
        width: 20px;
        height: 20px;
        background: url("../assets/icons/default/x_delete_icon.svg") no-repeat center;
        background-size: 100%;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          transform: rotate(180deg);
        }
      }
    }

    .models {
      height: 100%;

      &__list {
        overflow: auto;
        margin-bottom: 15px;
        max-height: calc(100% - 48px - 15px - 15px);
      }

      .button {
        margin: auto;
      }
    }
  }

  &__list {
    &--hide {
      display: none;
      // flex-basis: 0;
      // opacity: 0;
      // min-width: 0;
      // padding: 0;
      // margin-right: 0;
    }

    .companies {
      padding: 60px 10px 10px 10px;
      height: 100%;

      &__search {
        margin-bottom: 20px;

        .search {
          &__search {
            padding-left: 45px;
            width: 100%;
            background-position: 10px center;
          }
        }
      }

      &__list {
        padding-top: 10px;
        max-height: calc(100% - 40px - 20px);
        overflow: auto;
        display: flex;
        flex-wrap: wrap;
      }
    }
  }

  &__overview {
    &--toggled {
      margin-left: 0;
    }

    .contact {
      padding-top: 80px;
      height: 100%;
      display: flex;
      flex-direction: column;

      .company {
        flex: 1;

        &__header {
          background: var(--bg-17);
          padding: 12px 0;
          color: var(--text-9);
          font-size: var(--medium);
          text-align: center;
          position: relative;

          display: flex;
          align-items: center;
          justify-content: center;
        }

        &__logo {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          height: 81px;
          width: 165px;
          background: var(--bg-1);
          border-radius: var(--radius-1);
          box-shadow: 0px 3px 7px var(--shadow-1);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;

          img {
            max-height: 54px;
          }
        }


        &__actions {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;

          position: absolute;
          right: 10px;
        }

        &__contacts {
          padding: 30px 18px;
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 20px;
        }

        &__contact {
          flex: 1;
        }

        &__detail {
          margin-bottom: 8px;
          color: var(--text-9);
        }

        &__icon {
          height: 24px;
          width: 24px;
          margin-bottom: 20px;

          &--location {
            background: url("../assets/icons/default/location_icon.svg");
          }

          &--mobile {
            background: url("../assets/icons/default/mobile_icon.svg");
          }

          &--mail {
            background: url("../assets/icons/default/mail_icon.svg");
          }

          &--web {
            background: url("../assets/icons/default/web_icon.svg");
          }
        }
      }

      &__services {
        padding: 5px 15px;

        &--center {
          .services {
            justify-content: center;
          }
        }

        .services {
          overflow: auto;
          max-height: 184px;
          padding: 20px 10px 2px 10px;
          background: var(--bg-17);
          border-radius: var(--radius-1);
          display: flex;
          align-items: center;
          flex-wrap: wrap;

          .service {
            background: var(--bg-2);
            cursor: pointer;
            padding: 10px 5px;
            color: var(--text-1);
            margin: 0 18px 18px 0;
            border-radius: var(--radius-1);
            box-shadow: 0px 7px 18px var(--shadow-10);
            transition: all 0.3s ease;

            &:hover:not(.service--add) {
              background: var(--hover-1);
              box-shadow: 0px 0px 20px var(--shadow-10);
            }

            &--add {
              box-shadow: none;
              margin: 0 18px 18px 0;
            }
          }
        }
      }

      &__map-wrapper {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      &__map {
        padding: 0 15px 15px 15px;
        width: 100%;
        height: 100%;
        flex: 1;
      }
    }
  }

  .comment {
    margin: 20px 0;
    padding: 0 20px;
  }

  @media (max-width: 1300px) {
    .companies {
      margin-top: 20px;
    }

    .companies__list {
      height: 50vh;
      overflow: auto;
    }

    &__content {
      flex-direction: column;
      gap: 50px;
      height: auto;
      min-height: calc(100% - 48px - 20px - 10px);
      overflow: visible;
    }


    &__list,
    &__overview,
    &__models {
      width: 100%;
      flex: 0 0 auto;
      height: auto;
      min-height: 200px;
    }

    &__list {
      max-height: none;

      .companies {
        padding: 30px 10px 10px 10px;

        &__list {
          max-height: none;
        }
      }
    }

    &__models {
      padding: 0;
      margin: 0;
      padding-top: 20px;

      &--show {
        flex-basis: auto;
        padding-top: 20px;
      }
    }
  }

  @media (max-width: 900px) {
    .contacts__buttons {
      width: 100%;
      button {
        width: 100%;
        margin: 0 !important;
      } 
    }
    

    .icon__edit,
    .icon__delete {
      width: 24px !important;
      height: 24px !important;
    }

    &__content {
      padding-top: 20px;
      gap: 80px;
    }

    &__buttons {
      .button {
        width: 160px;
      }
    }

    &__overview {
      .contact {
        .company {
          &__contact {
            flex: 0 0 auto;
          }
        }

        &__map-wrapper {
          min-height: 320px;
        }
      }
    }
  }

  @media (max-width: 640px) {
 
    .contacts__list .companies {
      padding: 30px 10px;
    }

 
    &__overview {
      .contact {
        .company {
          &__header {
            padding: 20px 5px;
            justify-content: flex-start;
            align-items: flex-start;
            flex-wrap: wrap;
            gap: 10px;

            span {
              flex: 1 1 100%;
              line-break: anywhere;
              max-width: 100%;
              order: 2;
            }
          }

          &__logo {
            height: 60px;
            width: 120px;
            order: 1;
            top: 5px;
          }

          &__actions {
            margin-left: auto;
            position: relative;
            transform: initial;
            left: initial;
            top: initial;
            right: initial;
            order: 1;
          }

          &__logo,
          &__actions {
            flex: 0 0 auto;
          }
        }
      }
    }

    .comment {
      padding: 0 10px;
    }
  }
}
</style>
